import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useTopic } from '@pkg/components/Sections/Profile/pubSub';
import {
    glTouchEndTopic,
    glTouchMoveTopic,
    glTouchStartTopic,
} from '@pkg/components/Sections/Profile/pubSub/constants';
import { glUpdateFacesSubject } from '@pkg/components/Sections/Profile/pubSub/subjects';
import { useGetPortfolioLidTokensData } from '@pkg/hooks/liquid-id';
import { useMediaQuery } from '@pkg/providers';
import WebGL from '@pkg/webgl';
import { LID_LAYER_ID } from '@pkg/webgl/shared';

import { LIQUID_ID_FACES_LENGTH } from '@app/pages/Prototype/LiquidIDSection/config';
import { setNFTManagerSearchValue, updateNFTManagerFilters } from '@app/store/actions/nft-manager';
import { getLiquidIdCollectionItems } from '@app/store/actions/yat-page-items';
import { setLiquidIdEditStage, setYatPageLiquidID, updateYatPageEditStates } from '@app/store/slices';
import { LiquidIDEditStage } from '@app/store/slices/yat-page/types';

import { useAppDispatch, useAppSelector } from './redux';

const EMPTY_LIQUID_ID_ITEMS = Array(LIQUID_ID_FACES_LENGTH).fill(undefined);

type useStandAloneLiquidIdProps = {
    editMode?: boolean;
    standalone?: boolean;
    isScrollingElement?: boolean;
    wrapperRef: MutableRefObject<HTMLDivElement | null>;
    backgroundVisibility?: boolean;
};

export const useLiquidId = ({
    standalone,
    wrapperRef,
    editMode,
    isScrollingElement = true,
    backgroundVisibility = true,
}: useStandAloneLiquidIdProps) => {
    const { data: liquidIdData } = useGetPortfolioLidTokensData();
    const { tablet } = useMediaQuery();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { desktop } = useMediaQuery();
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);

    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const collectionId = useAppSelector((state) => state.nft.liquidIdCollectionId);

    const eid = useAppSelector((state) => state.user?.primaryYat);
    const glMouseDownRef = useRef(false);
    const glDraggingRef = useRef(false);

    useEffect(() => {
        if (standalone && collectionId && eid) {
            // Clear liquid id faces
            dispatch(setYatPageLiquidID({ items: EMPTY_LIQUID_ID_ITEMS }));
            dispatch(getLiquidIdCollectionItems({ collectionId, eid, pageId: null, standalone: true }));
        }
    }, [collectionId, dispatch, eid, standalone]);

    const updateLayer = useCallback(() => {
        setTimeout(() => {
            const layer = WebGL.getLayer(LID_LAYER_ID);
            if (layer) {
                WebGL.updateLayer(LID_LAYER_ID, wrapperRef.current, {
                    isScrollingElement: isScrollingElement,
                    isStickyBottom: !standalone && desktop && !liquidIDExpandedPreview,
                });

                layer.scene.setBackgroundVisibility(backgroundVisibility);
            }
        }, 100);
    }, [wrapperRef, isScrollingElement, standalone, desktop, liquidIDExpandedPreview, backgroundVisibility]);

    useEffect(() => {
        if (!wrapperRef?.current) return;

        if (WebGL.getLayer(LID_LAYER_ID)) return updateLayer();
        WebGL.loadLiquidID();
        WebGL.liquidIDReady().then(updateLayer);
    }, [wrapperRef, updateLayer, liquidIDExpandedPreview]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            glUpdateFacesSubject.next({ lidID: LID_LAYER_ID, isCancel: false, items: liquidIdData || [] });
        }, 50);
        return () => clearTimeout(timeout);
    }, [liquidIdData]);

    // Used to determine whether user is dragging for orbit controls or clicking to expand
    useTopic(glTouchStartTopic, () => {
        if (tablet) {
            glDraggingRef.current = false;
            glMouseDownRef.current = true;
        }
    });

    useTopic(glTouchMoveTopic, () => {
        if (tablet) glDraggingRef.current = true;
    });

    useTopic(glTouchEndTopic, () => {
        if (tablet) {
            glMouseDownRef.current = false;
            setTimeout(() => {
                glDraggingRef.current = false;
            }, 5);
        }
    });

    const handleExpandLiquidID = useCallback(() => {
        const canExpand = !editingLiquidID && !liquidIDExpandedPreview && !glDraggingRef.current && !standalone;
        if (!canExpand) return;

        if (editMode) {
            dispatch(setLiquidIdEditStage(LiquidIDEditStage.Selection));
            dispatch(updateYatPageEditStates({ editingLiquidID: true }));
            // Clear filters
            dispatch(setNFTManagerSearchValue(''));
            dispatch(updateNFTManagerFilters({ clear: true }));
        } else {
            navigate({ search: 'lid=1' });
        }
    }, [editingLiquidID, liquidIDExpandedPreview, standalone, editMode, dispatch, navigate]);

    return { handleExpandLiquidID };
};
